import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// 1. Imports de Routing (Necesarios para navegación y ubicación)
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// 2. Imports de UI (Material Design)
import { Button, Drawer, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';

// 3. Analytics (Solo para eventos, la inicialización ya está en App.js)
import ReactGA4 from 'react-ga4';

// 4. Iconos
import MapIcon from '@mui/icons-material/Map';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import LanguageIcon from '@mui/icons-material/Language';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

// 5. Vistas y Componentes
import ViewMap from '../views/view-map.js';
import PerformanceVisualization from '../views/view-performance.js';
import PlotlyChart from '../views/view-analytics.js';
import ForecastMetricsVisualization from '../views/view-forecast_metrics.js';
import TabName from './autotab-name.js';
import LoginModal from '../components/login/singup_login_modal.js';
import EmailVerified from '../components/login/email_verification.js';

// NOTA: La inicialización se hace ahora en el App.js padre.
// ReactGA4.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);
function ListItemTextStyle() {
  return {
    listItemTextSx: {
      '& .MuiTypography-root': {
        transition: 'transform 0.3s ease-in-out',
        '&:hover': { transform: 'scale(1.1)' },
      },
    },
  };
}

function NavigationDrawer({ open, toggleDrawer, userType }) {
  const navigate = useNavigate();
  const styles = ListItemTextStyle();

  const selectView = (path) => {
    // Aseguramos ruta completa para el drawer
    const fullPath = path.startsWith('/kairos') ? path : `/kairos${path}`;
    ReactGA4.event({ category: 'Navigation', action: 'Select View', label: fullPath });
    navigate(fullPath);
    toggleDrawer(false);
  };

  const openExternalLink = (url, label) => {
    ReactGA4.event({ category: 'Navigation', action: 'Open External', label });
    window.open(url, '_blank', 'noopener,noreferrer');
    toggleDrawer(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => { ReactGA4.event({ category: 'Navigation', action: 'Close Drawer' }); toggleDrawer(false); }}
      zIndex={1000}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '60%', sm: '35%', md: '25%', lg: '20%', xl: '18%' },
          height: '100%', color: 'rgb(0,127,255)', background: 'white', fontSize: 20,
        },
      }}
    >
      {/* ... Tu contenido del Drawer igual ... */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <h2 className="boton-menu-drawer">Menu</h2>
      </div>
      <div style={{ width: '100%', height: '1px', backgroundColor: '#ccc', marginTop: '-2.5px' }} />

      <List>
        {/* Nota: Aquí pasamos rutas relativas con barra, selectView se encarga del prefijo */}
        <ListItem onClick={() => selectView('/weather-map')} style={{ cursor: 'pointer' }}>
          <ListItemIcon><MapIcon /></ListItemIcon>
          <ListItemText primary="Map & Predictions" sx={styles.listItemTextSx} />
        </ListItem>
        {/* ... resto de items ... */}
      </List>
    </Drawer>
  );
}

const GrayGradientButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  top: '5vh',
  right: '2vw',
  zIndex: 10000,
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  fontWeight: 500,
  color: '#fff',
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '12px',
  padding: '8px 20px',
  boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-3px) scale(1.03)',
    boxShadow: '0 20px 48px rgba(0,0,0,0.4)',
    borderColor: 'rgba(255, 255, 255, 0.4)'
  },
  opacity: 1,

  // Móvil pequeño
  [theme.breakpoints.down('sm')]: {
    top: '6vh',
    right: '1.5vw',
    fontSize: '0em',
    padding: '1px 2px',
  },
  // Tablet / móvil grande
  [theme.breakpoints.between('sm', 'md')]: {
    top: '6.5vh',
    right: '1vw',
    fontSize: '0.65rem',
    padding: '6px 15px',
  },
  // Escritorio
  [theme.breakpoints.up('md')]: {
    top: '5.5vh',
    right: '0.5vw',
    fontSize: '0.75rem',
    padding: '8px 20px',
  },
}));

export function ForecastMetricsButton() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (!['/', '/weather-map'].includes(pathname)) return null;

  return (
    <GrayGradientButton
      startIcon={<QueryStatsIcon />}
      onClick={() => navigate('/forecast-metrics')}
    >
      Forecast Metrics
    </GrayGradientButton>
  );
}

// --- COMPONENTE PRINCIPAL ---
function KairosMain() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [userType, setUserType] = useState(null);
  const [, setToken] = useState(null);
  
  // Idle Timer (Se queda aquí de momento para proteger solo Kairos)
  const lastActivityRef = useRef(Date.now());
  const timeoutIdRef = useRef(null);

  useEffect(() => {
    const THRESHOLD = 3600000; // 1 hora
    const reloadIfIdle = () => {
      if (Date.now() - lastActivityRef.current >= THRESHOLD) {
        window.location.reload();
      } else {
        scheduleReload();
      }
    };
    const scheduleReload = () => {
      clearTimeout(timeoutIdRef.current);
      const timeLeft = THRESHOLD - (Date.now() - lastActivityRef.current);
      timeoutIdRef.current = setTimeout(reloadIfIdle, timeLeft);
    };
    const activityEvents = ['click', 'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'touchmove'];
    const onActivity = () => {
      lastActivityRef.current = Date.now();
      scheduleReload();
    };
    activityEvents.forEach(evt => window.addEventListener(evt, onActivity));
    const onVisibility = () => { if (!document.hidden) reloadIfIdle(); };
    document.addEventListener('visibilitychange', onVisibility);
    scheduleReload();
    return () => {
      clearTimeout(timeoutIdRef.current);
      activityEvents.forEach(evt => window.removeEventListener(evt, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const handleLoginSuccess = (type, tokenValue) => {
    ReactGA4.event({ category: 'User', action: 'Login Success', label: type });
    setUserType(type);
    setToken(tokenValue);
    setLoginModalOpen(false);
    setDrawerOpen(true);
  };

  return (
    <>
      {/* 🗑️ BORRADO: <PageTracker /> */}
      <TabName />
      
      {userType && (
        <NavigationDrawer open={drawerOpen} toggleDrawer={setDrawerOpen} userType={userType} />
      )}
      
      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} onLoginSuccess={handleLoginSuccess} />
      
      <Routes>
        {/* 🔥 IMPORTANTE: Rutas sin '/' al inicio para anidamiento correcto */}
        <Route path="weather-map" element={<><ViewMap /></>} />
        <Route path="performance" element={<PerformanceVisualization />} />
        <Route path="analytics" element={<PlotlyChart />} />
        <Route path="forecast-metrics" element={<ForecastMetricsVisualization />} />
        <Route path="email-verified" element={<EmailVerified onLoginSuccess={handleLoginSuccess} />} />
        
        {/* Redirecciones internas relativas */}
        <Route path="/" element={<Navigate replace to="weather-map" />} />
        <Route path="index.html" element={<Navigate replace to="weather-map" />} />
        <Route path="*" element={<Navigate replace to="weather-map" />} />
      </Routes>
    </>
  );
}

export default KairosMain;